/**
 * Mobile menu component
 * Simple hamburger menu implementation for mobile devices
 */

class MobileMenu {
  constructor() {
    this.isMenuOpen = false
    this.SELECTORS = {
      menuButton: '.menu-toggle',
      menuContainer: '.site-navigation',
      menuOverlay: '.menu-overlay',
      closeButton: '[data-close-menu]',
      submenuToggles: '.submenu-toggle',
    }

    this.menuButton = null
    this.menuContainer = null
    this.menuOverlay = null
    this.closeButton = null
    this.submenuToggles = []
  }

  /**
   * Initialize the mobile menu
   * @returns {Promise<boolean>} True if initialization was successful
   */
  async init() {
    try {
      // Find DOM elements
      this.menuButton = document.querySelector(this.SELECTORS.menuButton)
      this.menuContainer = document.querySelector(this.SELECTORS.menuContainer)
      this.menuOverlay = document.querySelector(this.SELECTORS.menuOverlay)
      this.closeButton = document.querySelector(this.SELECTORS.closeButton)
      this.submenuToggles = document.querySelectorAll(this.SELECTORS.submenuToggles)

      // Check if required elements exist
      if (!this.menuButton || !this.menuContainer) {
        // Elements not found, likely not on a page that needs a mobile menu
        console.warn('Mobile menu elements not found, skipping initialization')
        return false
      }

      // Add initialization class after elements are found
      this.menuContainer.classList.add('is-initialized')

      // Setup event listeners
      this.menuButton.addEventListener('click', () => this.toggleMenu())

      // Add close button event listener
      if (this.closeButton) {
        this.closeButton.addEventListener('click', () => this.closeMenu())
      }

      if (this.menuOverlay) {
        this.menuOverlay.addEventListener('click', () => this.closeMenu())
      }

      // Setup submenu toggles
      if (this.submenuToggles.length > 0) {
        this.submenuToggles.forEach((toggle) => {
          toggle.addEventListener('click', (e) => {
            e.preventDefault()
            e.stopPropagation()
            this.toggleSubmenu(toggle)
          })
        })
      }

      // Close menu on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isMenuOpen) {
          this.closeMenu()
        }
      })

      // Setup any menu links to close the menu when clicked (but not submenu toggles)
      const menuLinks = this.menuContainer.querySelectorAll('a:not(.submenu-toggle)')
      menuLinks.forEach((link) => {
        link.addEventListener('click', () => this.closeMenu())
      })

      // Enable transitions after a small delay to ensure the initial state is set
      setTimeout(() => {
        document.body.classList.add('menu-transitions-enabled')
      }, 100)

      // Handle clicks on document
      document.addEventListener('mousedown', (event) => {
        const isMenuOpen = this.menuContainer.classList.contains('is-active')
        const clickedOutside
          = !this.menuContainer.contains(event.target) && !this.menuButton.contains(event.target)

        if (isMenuOpen && clickedOutside) {
          this.closeMenu()
        }
      })

      return true
    }
    catch (error) {
      console.error('Error initializing mobile menu:', error)
      return false
    }
  }

  /**
   * Toggle the mobile menu open/closed
   */
  toggleMenu() {
    if (!this.menuContainer) return

    this.isMenuOpen = !this.isMenuOpen

    // Toggle aria-expanded for accessibility
    this.menuButton.setAttribute('aria-expanded', this.isMenuOpen)

    // Toggle classes for animation
    this.menuContainer.classList.toggle('is-active', this.isMenuOpen)
    this.menuOverlay.classList.toggle('hidden', !this.isMenuOpen)
    this.menuOverlay.classList.toggle('opacity-0', !this.isMenuOpen)
    this.menuOverlay.classList.toggle('opacity-50', this.isMenuOpen)

    // Prevent body scrolling when menu is open
    document.body.classList.toggle('overflow-hidden', this.isMenuOpen)
  }

  /**
   * Toggle a submenu open/closed
   * @param {HTMLElement} toggle - The submenu toggle element
   */
  toggleSubmenu(toggle) {
    // Find the parent li element
    const parentLi = toggle.closest('.has-submenu')
    if (!parentLi) return

    // Find the submenu
    const submenu = parentLi.querySelector('.submenu')
    if (!submenu) return

    // Check if we're on mobile (up to lg breakpoint)
    const isMobile = window.innerWidth < 1024
    if (!isMobile) return // Only toggle submenu on mobile

    const isOpen = toggle.getAttribute('aria-expanded') === 'true'
    const newState = !isOpen

    // Toggle the submenu classes
    toggle.setAttribute('aria-expanded', String(newState))
    submenu.classList.toggle('is-open', newState)
  }

  /**
   * Close the mobile menu
   */
  closeMenu() {
    if (!this.isMenuOpen || !this.menuContainer) return

    this.isMenuOpen = false

    // Update aria-expanded for accessibility
    this.menuButton.setAttribute('aria-expanded', 'false')

    // Update classes for animation
    this.menuContainer.classList.remove('is-active')
    this.menuOverlay.classList.add('hidden', 'opacity-0')
    this.menuOverlay.classList.remove('opacity-50')

    // Re-enable body scrolling
    document.body.classList.remove('overflow-hidden')
  }
}

// Create singleton instance
const mobileMenu = new MobileMenu()

/**
 * Initialize the mobile menu and return the instance
 * @returns {Promise<MobileMenu|null>} The mobile menu instance or null if initialization failed
 */
export async function initMobileMenu() {
  const success = await mobileMenu.init()
  return success ? mobileMenu : null
}

// Export the class for direct usage if needed
export { MobileMenu }
